import React, { useEffect, useRef, useState } from 'react';
import { X, Save, RotateCcw, Loader } from '../lib/icons';
import type { ContentEditRequest, } from '../contexts/EditModeContext';
import type { ContentType } from '../lib/content-slots-client';
import { renderRichContent } from '../lib/content-slots-client';

interface ContentEditorProps {
  request: ContentEditRequest;
  onSave: (content: string) => Promise<void>;
  onReset: () => Promise<void>;
  onClose: () => void;
}

const TYPE_LABELS: Record<ContentType, string> = {
  heading:    'Heading',
  subheading: 'Subheading',
  paragraph:  'Paragraph',
  button:     'Button Label',
  label:      'Label',
  caption:    'Caption',
};

const IS_RICH: Record<ContentType, boolean> = {
  heading:    false,
  subheading: false,
  paragraph:  true,
  button:     false,
  label:      false,
  caption:    false,
};

function wrapSelection(
  textarea: HTMLTextAreaElement,
  before: string,
  after: string,
  setValue: (v: string) => void,
) {
  const { selectionStart: start, selectionEnd: end, value } = textarea;
  const selected = value.slice(start, end);
  const newValue = value.slice(0, start) + before + selected + after + value.slice(end);
  setValue(newValue);
  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(start + before.length, end + before.length);
  }, 0);
}

export default function ContentEditor({ request, onSave, onReset, onClose }: ContentEditorProps) {
  const { slotId, currentContent, fallback, contentType, label } = request;
  const isRich = IS_RICH[contentType];
  const isMultiline = isRich;

  const [value, setValue] = useState(currentContent);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isModified = value !== currentContent;
  const isEmpty = value.trim() === '';

  useEffect(() => {
    const el = isMultiline ? textareaRef.current : inputRef.current;
    if (el) {
      el.focus();
      if ('setSelectionRange' in el) {
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }
  }, [isMultiline]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); handleSave(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(value);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setError(null);
    try {
      await onReset();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setResetting(false);
    }
  };

  const boldSelected = () => {
    if (textareaRef.current) wrapSelection(textareaRef.current, '**', '**', setValue);
  };
  const italicSelected = () => {
    if (textareaRef.current) wrapSelection(textareaRef.current, '*', '*', setValue);
  };
  const bulletLine = () => {
    if (!textareaRef.current) return;
    const { selectionStart: start, value: v } = textareaRef.current;
    const lineStart = v.lastIndexOf('\n', start - 1) + 1;
    const newValue = v.slice(0, lineStart) + '- ' + v.slice(lineStart);
    setValue(newValue);
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(start + 2, start + 2);
    }, 0);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#0D0D0D] border border-gray-800 rounded-2xl w-full max-w-2xl shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-800">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-[#39FF14]">
                {TYPE_LABELS[contentType]}
              </span>
              {label && label !== slotId && (
                <span className="text-xs text-[#E0E0E0] font-semibold truncate">{label}</span>
              )}
            </div>
            <p className="text-[10px] text-gray-600 font-mono truncate mt-0.5">{slotId}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-[#E0E0E0] transition-colors ml-4 shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Rich text toolbar */}
          {isRich && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={boldSelected}
                className="px-2.5 py-1 rounded text-xs font-bold text-[#B0B0B0] hover:text-[#E0E0E0] hover:bg-gray-800 transition-colors"
                title="Bold — wrap selection in **"
              >
                B
              </button>
              <button
                type="button"
                onClick={italicSelected}
                className="px-2.5 py-1 rounded text-xs italic text-[#B0B0B0] hover:text-[#E0E0E0] hover:bg-gray-800 transition-colors"
                title="Italic — wrap selection in *"
              >
                I
              </button>
              <button
                type="button"
                onClick={bulletLine}
                className="px-2.5 py-1 rounded text-xs text-[#B0B0B0] hover:text-[#E0E0E0] hover:bg-gray-800 transition-colors"
                title="Bullet — prefix line with -"
              >
                •
              </button>
              <div className="ml-auto">
                <button
                  type="button"
                  onClick={() => setShowPreview(p => !p)}
                  className={`text-[10px] px-2.5 py-1 rounded border transition-colors ${
                    showPreview
                      ? 'border-[#39FF14]/50 text-[#39FF14]'
                      : 'border-gray-700 text-gray-500 hover:text-[#E0E0E0]'
                  }`}
                >
                  Preview
                </button>
              </div>
            </div>
          )}

          {/* Editor */}
          {isMultiline ? (
            showPreview ? (
              <div
                className="min-h-[140px] max-h-[320px] overflow-y-auto bg-gray-900 rounded-xl border border-gray-700 px-4 py-3 text-sm text-[#E0E0E0] leading-relaxed whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: renderRichContent(value) }}
              />
            ) : (
              <textarea
                ref={textareaRef}
                value={value}
                onChange={e => setValue(e.target.value)}
                rows={6}
                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-[#E0E0E0] font-mono leading-relaxed resize-y focus:outline-none focus:border-[#39FF14] transition-colors placeholder-gray-600"
                placeholder="Enter text… use **bold**, *italic*, - bullet"
                spellCheck
              />
            )
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#39FF14] transition-colors placeholder-gray-600"
              placeholder="Enter text…"
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
              }}
              spellCheck
            />
          )}

          {/* Default text */}
          <div className="flex items-start gap-2 text-xs text-gray-600">
            <span className="shrink-0 font-semibold mt-0.5">Default:</span>
            <span className="italic line-clamp-2">{fallback}</span>
          </div>

          {error && (
            <p className="text-red-400 text-xs">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-gray-800">
          <button
            onClick={handleReset}
            disabled={resetting || !slot_has_db_value(currentContent, fallback)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {resetting ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            Reset to Default
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3.5 py-1.5 rounded-lg text-xs text-gray-400 hover:text-[#E0E0E0] border border-gray-700 hover:border-gray-600 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || isEmpty}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-[#39FF14] text-black hover:bg-[#00BFFF] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            >
              {saving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper: Reset to Default is only active when there's a Supabase value to clear.
// We can infer this when currentContent differs from fallback (a custom value was saved).
function slot_has_db_value(currentContent: string, fallback: string): boolean {
  return currentContent !== fallback;
}
